import { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import {
  MDBInputGroup,
  MDBInput,
  MDBRow,
  MDBCol,
  MDBIcon,
} from "mdb-react-ui-kit";
import { format } from "date-fns";
import { fetchLegislatorBills } from "../../../../utils/billOperations";
import ExportDropdown from "../../../common/ExportDropdown";
import { exportToCSV, exportToPDF } from "../../../../utils/exportDatatable";

const Bills = ({ legislator }) => {

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    billNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    lcNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    affiliation: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shortTitle: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.CONTAINS },
    statusDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    const fetchBills = async () => {
      setLoading(true);
      const bills = await fetchLegislatorBills(legislator);

      const enriched = bills.content.map((bill) => {
        const latestStatus = bill.draft?.billStatuses?.sort(
          (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
        )[0];

        return {
          ...bill,
          billNumber: `${bill.billType?.code} ${bill.billNumber}`,
          lcNumber: bill.draft?.draftNumber,
          affiliation: (() => {
            const code = bill.billType?.code;
            const chamber = legislator?.legislatorDetails?.chamber?.toUpperCase();
            const isSenate = ["SB", "SJ", "SR"].includes(code);
            const isHouse = ["HB", "HJ", "HR"].includes(code);
            if (isSenate) {
              return chamber == "SENATE" ? "Sponsor" : "Carrier";
            }
            if (isHouse){
              return chamber == "HOUSE" ? "Sponsor" : "Carrier";
            } 
            return "Sponsor";
          })(),
          shortTitle: bill.draft?.shortTitle,
          status: latestStatus?.billStatusCode?.name || "",
          statusDate: latestStatus?.timeStamp
            ? format(new Date(latestStatus.timeStamp), "MM/dd/yy")
            : "",
        };
      });

      setBills(enriched);
      setLoading(false);
    };

    fetchBills();
  }, [legislator]);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const lcNumberBody = (rowData) => (
    <a
      href={`${process.env.REACT_APP_BILLS_EXPLORER}/#/lc/bill/${rowData.sessionId}/${rowData.draft?.draftNumber}`}
      target="_blank"
    >
      {rowData.draft?.draftNumber}
    </a>
  );

  const billNumberBody = (rowData) =>  (
    <a
      href={`${process.env.REACT_APP_BILLS_EXPLORER}/#/laws/bill/${rowData.sessionId}/${rowData.draft?.draftNumber}`}
      target="_blank"
    >
      {rowData.billNumber}
    </a> 
  ); 

  const affiliationBody = (rowData) => {
    const billType = rowData.billType?.code;
    const legislatorChamber = legislator?.legislatorDetails?.chamber?.toUpperCase();
    
    const isSenateBill = ['SB', 'SJ', 'SR'].includes(billType);
    const isHouseBill = ['HB', 'HJ', 'HR'].includes(billType);
    
    if (isSenateBill) {
      return legislatorChamber === 'SENATE' ? 'Sponsor' : 'Carrier';
    }
     
    if (isHouseBill) {
      return legislatorChamber === 'HOUSE' ? 'Sponsor' : 'Carrier';
    }

    return 'Sponsor';
  };

  const exportColumns = [
    { field: "billNumber", header: "Bill #" },
    { field: "lcNumber", header: "LC #" },
    { field: "affiliation", header: "Affiliation" },
    { field: "shortTitle", header: "Short Title" },
    { field: "status", header: "Status" },
    { field: "statusDate", header: "Status Date" },
  ];

  const handleExport = (type) => {
    if (type === "csv") {
      exportToCSV(`${legislator?.legislatorDetails?.lastName}-${legislator?.legislatorDetails?.firstName}-Bills`, exportColumns, bills);
    } else if (type === "pdf") {
      exportToPDF(`${legislator?.legislatorDetails?.lastName}-${legislator?.legislatorDetails?.firstName}-Bills`, exportColumns, bills, {});
    }
  };

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <MDBInput
              type="text"
              label="Search Bills"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
        <MDBCol size="auto">
          <ExportDropdown
            data={bills}
            onExport={handleExport}
            columns={exportColumns}
            fileName={`bills-${legislator?.lastName || "export"}`}
          />
        </MDBCol>
      </MDBRow>
      <DataTable
        value={bills}
        showGridlines
        stripedRows
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="650px"
        sortField="billNumber"
        sortOrder={1}
        removableSort
        loading={loading}
        filters={filters}
        globalFilterFields={[
          "billNumber",
          "lcNumber",
          "affiliation",
          "shortTitle",
          "status",
          "statusDate",    
        ]}
        emptyMessage="No bills found."
      >
        <Column
          header="Bill #"
          field="billNumber"
          body={billNumberBody}
          sortable
          filter
          filterPlaceholder="Search Bill #"
        />
        <Column
          header="LC #"
          field="lcNumber"
          body={lcNumberBody}
          sortable
          filter
          filterPlaceholder="Search LC #"
        />
        <Column
          header="Affiliation"
          field="affiliation"
          body={affiliationBody}
          sortable
          filter
          filterPlaceholder="Search Affiliation"
        />
        <Column
          header="Short Title"
          field="shortTitle"
          body={(rowData) => (
            <span
              className="short-title"
              title={rowData.draft?.shortTitle}
              style={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {rowData.draft?.shortTitle || " "}
            </span>
          )}
          sortable
          filter
          filterPlaceholder="Search Short Title"
        />
        <Column
          header="Status"
          field="status"
          sortable
          filter
          filterPlaceholder="Status"
        />
        <Column
          header="Status Date"
          field="statusDate"
          sortable
          filter
          filterPlaceholder="Search Status Date"
        />
      </DataTable>
    </>
  );
};

export default Bills;
